import { useEffect, useState } from "react";

/**
 * Phone layout switch (29 Sep 2026): the floating glass header, the slim
 * closed input bar and swipe-to-open-sidebar exist only at this width. The
 * same query scopes styles/mobile-glass.css, so desktop never changes.
 */
export const PHONE_QUERY = "(max-width: 640px)";

function matches(): boolean {
  return typeof window !== "undefined" && typeof window.matchMedia === "function"
    && window.matchMedia(PHONE_QUERY).matches;
}

export function useIsPhone(): boolean {
  const [phone, setPhone] = useState(matches);
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const query = window.matchMedia(PHONE_QUERY);
    const update = () => setPhone(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);
  return phone;
}

/** Elements that own horizontal drags (code blocks, carousels, sliders, text fields). */
function ownsHorizontalDrag(target: EventTarget | null): boolean {
  let el = target instanceof Element ? target : null;
  // A sheet, modal or the image preview owns every gesture inside it.
  if (el?.closest("dialog, [role='dialog'], [aria-modal='true'], .lightbox")) return true;
  while (el && el !== document.body) {
    if (el.matches("input, textarea, select, [contenteditable='true'], [data-no-swipe]")) return true;
    if (el.scrollWidth > el.clientWidth + 1) {
      const overflowX = getComputedStyle(el).overflowX;
      if (overflowX === "auto" || overflowX === "scroll") return true;
    }
    el = el.parentElement;
  }
  return false;
}

/** Swipe distance (px) and how horizontal it must be before it counts. */
const SWIPE_MIN_DX = 64;
const SWIPE_MAX_SLOPE = 0.6;

/**
 * Phones only: swipe right anywhere on the chat opens the chats sidebar, swipe
 * left while it is open closes it. A vertical scroll, a drag inside a text
 * field or a horizontally scrolling block never counts, and nothing is
 * cancelled (listeners are passive), so taps and scrolling behave as before.
 */
export function usePhoneSidebarSwipe(enabled: boolean, open: boolean, setOpen: (open: boolean) => void) {
  useEffect(() => {
    if (!enabled) return;
    let start: { x: number; y: number } | null = null;
    let fired = false;
    const onStart = (event: TouchEvent) => {
      fired = false;
      const t = event.touches[0];
      // An open sheet or modal (even one whose backdrop is not itself a dialog) owns the screen.
      const covered = !!document.querySelector("dialog[open], [aria-modal='true'], .wf-overlay, .reviewSheet, .historyConfirm__backdrop");
      start = event.touches.length === 1 && t && !covered && !ownsHorizontalDrag(event.target) ? { x: t.clientX, y: t.clientY } : null;
    };
    const onMove = (event: TouchEvent) => {
      const t = event.touches[0];
      if (!start || fired || !t) return;
      const dx = t.clientX - start.x;
      const dy = Math.abs(t.clientY - start.y);
      if (Math.abs(dx) < SWIPE_MIN_DX || dy > Math.abs(dx) * SWIPE_MAX_SLOPE) return;
      if (!open && dx > 0) {
        fired = true;
        // Drop the keyboard first, so typing can't land in the hidden composer.
        (document.activeElement as HTMLElement | null)?.blur?.();
        setOpen(true);
      }
      else if (open && dx < 0) { fired = true; setOpen(false); }
    };
    const onEnd = () => { start = null; };
    document.addEventListener("touchstart", onStart, { passive: true });
    document.addEventListener("touchmove", onMove, { passive: true });
    document.addEventListener("touchend", onEnd, { passive: true });
    document.addEventListener("touchcancel", onEnd, { passive: true });
    return () => {
      document.removeEventListener("touchstart", onStart);
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("touchend", onEnd);
      document.removeEventListener("touchcancel", onEnd);
    };
  }, [enabled, open, setOpen]);
}

/**
 * Phones: mark <html data-kb="open"> while an on-screen keyboard covers the
 * bottom (visualViewport clearly shorter than the window). mobile-glass.css uses
 * it only to drop the home-indicator padding under the input bar and to hide
 * the jump-to-latest arrow while typing.
 *
 * It deliberately does NOT move or resize the app: pinning the app to the
 * visible area with a transform (tried 29 Sep) made iOS draw the caret in the
 * wrong place and fight scrolling. iOS's own behaviour — the page slides up so
 * the bar sits on the keyboard and the header scrolls out of view while you
 * type, then everything returns on ✓ — is what Build does and what reads best.
 */
/** env(safe-area-inset-top) in px: > 0 only when the page is drawn under the status bar. */
function safeTop(): number {
  const probe = document.createElement("div");
  probe.style.cssText = "position:fixed;top:0;left:0;width:0;height:env(safe-area-inset-top,0px);visibility:hidden;pointer-events:none";
  document.body.appendChild(probe);
  const h = probe.getBoundingClientRect().height;
  probe.remove();
  return h;
}

export function usePhoneViewport(enabled: boolean) {
  useEffect(() => {
    const vv = window.visualViewport;
    const root = document.documentElement;
    if (!enabled || !vv) return;
    let frame = 0;
    const apply = () => {
      frame = 0;
      if (vv.scale <= 1.01 && window.innerHeight - vv.height > 120) root.dataset.kb = "open";
      else {
        delete root.dataset.kb;
        // The installed iPhone app: 100dvh can come out one status bar short of the screen, so
        // the input bar floated ~50pt above where it belongs (29 Sep, vs ChatGPT). The layout
        // viewport (innerHeight) is the real screen and does not change with the keyboard, so the
        // tap-to-lift behaviour is untouched. When dvh is already right, this is the same number.
        // iOS 26 home-screen app: the layout viewport can stop short of the screen bottom (a
        // band under the splash, the bar ~50pt too high). Portrait + standalone + a short gap of
        // status-bar size only; anything else keeps innerHeight.
        const standalone = window.matchMedia("(display-mode: standalone)").matches ||
          (navigator as Navigator & { standalone?: boolean }).standalone === true;
        const portrait = window.matchMedia("(orientation: portrait)").matches;
        const gap = window.screen.height - window.innerHeight;
        // Only when the page runs UNDER the status bar (translucent, safe-area top > 0). With an
        // opaque status bar the page is honestly one status bar shorter than the screen, and
        // screen.height would push the bar below the bottom edge.
        const underStatusBar = safeTop() > 0;
        const h = standalone && portrait && underStatusBar && gap > 16 && gap < 120 ? window.screen.height : window.innerHeight;
        root.style.setProperty("--app-vh", `${h}px`);
      }
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(apply); };
    apply();
    vv.addEventListener("resize", schedule);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      vv.removeEventListener("resize", schedule);
      delete root.dataset.kb;
    };
  }, [enabled]);
}
